import { c as _c } from "react/compiler-runtime";
import { getErrorCode, getHostedIdentifySessionRequirementsOptions, postHostedIdentifySessionChallengeMutation, postHostedIdentifySessionChallengeOptions, postHostedIdentifySessionChallengeVerifyMutation, postHostedUserConsentMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { ApiError, IdentifyChallengeResponse } from '@onefootprint/request-types';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getDisplayEmail, getDisplayPhone } from '@/idv2/features/identify/get-display-contact-info';
import { isSessionExpired } from '@/idv2/features/identify/handle-session-expired';
import { OtpChallenge } from '@/idv2/features/identify/otp-challenge';
import { VerifiedPrefillConsent } from '@/idv2/features/identify/verified-prefill-consent';
import { useFootprint } from '@/idv2/hooks/use-footprint';
const SUCCESS_EVENT_DELAY_MS = 1500;
const getRetryAfterDate = (seconds: number) => new Date(Date.now() + seconds * 1000);
const otpParamsSchema = z.object({
  challengeKind: z.enum(['sms', 'email'])
});
const otpSearchSchema = z.object({
  email: z.string().optional(),
  phone: z.string().optional(),
  scrubbedEmail: z.string().optional(),
  scrubbedPhone: z.string().optional(),
  fromChallengeSelector: z.boolean().optional()
});
export const Route = createFileRoute('/identify/otp/$challengeKind')({
  component: OtpPage,
  parseParams: otpParamsSchema.parse,
  validateSearch: otpSearchSchema,
  loaderDeps: ({
    search
  }) => ({
    search
  }),
  loader: async ({
    context,
    params: {
      challengeKind
    },
    deps: {
      search
    }
  }) => {
    const headers = {
      'X-Fp-Authorization': context.onboardingSessionToken
    };
    const requirementsData = await context.queryClient.ensureQueryData(getHostedIdentifySessionRequirementsOptions({
      headers
    }));
    let challengeData: IdentifyChallengeResponse | undefined;
    let initialRetryDisabledUntil: Date | undefined;
    try {
      challengeData = await context.queryClient.ensureQueryData(postHostedIdentifySessionChallengeOptions({
        headers,
        body: {
          challengeKind
        }
      }));
    } catch (error) {
      if (getErrorCode(error) === 'E104') {
        const errorContext = (error as ApiError)?.context;
        const seconds = errorContext?.seconds ? Number(errorContext.seconds) : 0;
        initialRetryDisabledUntil = getRetryAfterDate(seconds);
      } else {
        throw error;
      }
    }
    const loginRequirement = requirementsData?.requirements?.[0]?.kind === 'login' ? requirementsData.requirements[0] : undefined;
    const displayTermsAndConditions = requirementsData?.displayTermsAndConditions ?? false;
    const verifiedPrefillContext = requirementsData?.verifiedPrefillContext;
    const isReturningUser = !!loginRequirement?.user && !loginRequirement.user.isUnverified && !search.fromChallengeSelector;
    const contactInfo = challengeKind === 'email' ? getDisplayEmail({
      user: loginRequirement?.user,
      email: search.email ?? search.scrubbedEmail
    }) : getDisplayPhone({
      user: loginRequirement?.user,
      phoneNumber: search.phone ?? search.scrubbedPhone
    });
    return {
      challengeData,
      contactInfo,
      displayTermsAndConditions,
      initialRetryDisabledUntil,
      isReturningUser,
      verifiedPrefillContext
    };
  }
});
function OtpPage() {
  const $ = _c(39);
  const footprint = useFootprint();
  const {
    toast
  } = useToast();
  const {
    onboardingSessionToken
  } = Route.useRouteContext();
  const {
    challengeKind
  } = Route.useParams();
  const {
    t
  } = useTranslation("idv2");
  const {
    getErrorCode,
    getErrorContext
  } = useRequestError();
  const {
    challengeData,
    contactInfo,
    displayTermsAndConditions,
    initialRetryDisabledUntil: loaderRetryUntil,
    isReturningUser,
    verifiedPrefillContext
  } = Route.useLoaderData();
  const initialTimeBeforeRetryS = challengeData?.challengeData?.timeBeforeRetryS;
  let t0;
  if ($[0] !== initialTimeBeforeRetryS || $[1] !== loaderRetryUntil) {
    t0 = () => {
      if (loaderRetryUntil) {
        return loaderRetryUntil;
      }
      return typeof initialTimeBeforeRetryS === "number" ? getRetryAfterDate(initialTimeBeforeRetryS) : undefined;
    };
    $[0] = initialTimeBeforeRetryS;
    $[1] = loaderRetryUntil;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [retryDisabledUntil, setRetryDisabledUntil] = useState(t0);
  const [verifiedPrefillConsentGiven, setVerifiedPrefillConsentGiven] = useState(false);
  const headers = {
    "X-Fp-Authorization": onboardingSessionToken
  };
  const showVerifiedPrefillConsent = challengeKind === "sms" && !!verifiedPrefillContext?.isEnabled && !!verifiedPrefillContext?.requiresConsent && !verifiedPrefillConsentGiven;
  const verifyMutation = useMutation(postHostedIdentifySessionChallengeVerifyMutation({
    headers
  }));
  const resendMutation = useMutation(postHostedIdentifySessionChallengeMutation({
    headers
  }));
  const consentMutation = useMutation(postHostedUserConsentMutation({
    headers
  }));
  const challengeHasError = !!resendMutation.data?.error || !!challengeData?.error;
  let t1;
  if ($[3] !== challengeHasError || $[4] !== t) {
    t1 = challengeHasError ? t(_temp) : undefined;
    $[3] = challengeHasError;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const hint = t1;
  let t2;
  if ($[6] !== challengeKind || $[7] !== getErrorCode || $[8] !== getErrorContext || $[9] !== resendMutation || $[10] !== t || $[11] !== toast) {
    t2 = async () => {
      ;
      try {
        const data = await resendMutation.mutateAsync({
          body: {
            challengeKind
          }
        });
        setRetryDisabledUntil(getRetryAfterDate(data.challengeData.timeBeforeRetryS));
        toast({
          title: t(_temp2),
          description: t(_temp3)
        });
      } catch (t3) {
        const error = t3;
        if (isSessionExpired(error)) {
          throw error;
        }
        const code = getErrorCode(error);
        if (code === "E104") {
          const ctx = getErrorContext(error);
          const seconds = typeof ctx?.seconds === "number" ? ctx.seconds : 0;
          setRetryDisabledUntil(getRetryAfterDate(seconds));
          toast({
            title: t(_temp4),
            description: t(_temp5),
            variant: "error"
          });
        }
      }
    };
    $[6] = challengeKind;
    $[7] = getErrorCode;
    $[8] = getErrorContext;
    $[9] = resendMutation;
    $[10] = t;
    $[11] = toast;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleResend = t2;
  let t3;
  if ($[13] !== consentMutation || $[14] !== displayTermsAndConditions || $[15] !== footprint || $[16] !== getErrorCode || $[17] !== handleResend || $[18] !== t || $[19] !== toast || $[20] !== verifyMutation) {
    t3 = async (challengeResponse, onError) => {
      ;
      try {
        await verifyMutation.mutateAsync({
          body: {
            challengeResponse
          }
        });
        if (displayTermsAndConditions) {
          await consentMutation.mutateAsync({
            body: {
              content: t(_temp6),
              kind: "terms_and_conditions"
            }
          });
        }
        setTimeout(() => {
          footprint.navigation.forward();
        }, SUCCESS_EVENT_DELAY_MS);
      } catch (t4) {
        const error_0 = t4;
        if (isSessionExpired(error_0)) {
          throw error_0;
        }
        const code_0 = getErrorCode(error_0);
        if (code_0 === "E103") {
          await handleResend();
          toast({
            title: t(_temp7),
            description: t(_temp8),
            variant: "error"
          });
          return;
        }
        onError(code_0 === "E102" ? t(_temp9) : t(_temp10));
      }
    };
    $[13] = consentMutation;
    $[14] = displayTermsAndConditions;
    $[15] = footprint;
    $[16] = getErrorCode;
    $[17] = handleResend;
    $[18] = t;
    $[19] = toast;
    $[20] = verifyMutation;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  const handleSubmit = t3;
  const isVerifying = verifyMutation.isPending || displayTermsAndConditions && consentMutation.isPending;
  if (showVerifiedPrefillConsent && verifiedPrefillContext) {
    let t4;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = () => setVerifiedPrefillConsentGiven(true);
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    let t5;
    if ($[23] !== onboardingSessionToken || $[24] !== verifiedPrefillContext) {
      t5 = <VerifiedPrefillConsent onboardingSessionToken={onboardingSessionToken} verifiedPrefillContext={verifiedPrefillContext} onContinue={t4} />;
      $[23] = onboardingSessionToken;
      $[24] = verifiedPrefillContext;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    return t5;
  }
  let t4;
  if ($[26] !== challengeKind || $[27] !== contactInfo || $[28] !== displayTermsAndConditions || $[29] !== handleResend || $[30] !== handleSubmit || $[31] !== hint || $[32] !== isReturningUser || $[33] !== isVerifying || $[34] !== resendMutation.isPending || $[35] !== retryDisabledUntil || $[36] !== verifyMutation.isPending || $[37] !== verifyMutation.isSuccess) {
    t4 = <OtpChallenge challengeKind={challengeKind} contactInfo={contactInfo} hint={hint} isReturningUser={isReturningUser} onSubmit={handleSubmit} isLoading={isVerifying} isResendLoading={resendMutation.isPending} isVerifying={verifyMutation.isPending} isSuccess={verifyMutation.isSuccess} onResend={handleResend} resendDisabledUntil={retryDisabledUntil} showLegalFooter={displayTermsAndConditions} />;
    $[26] = challengeKind;
    $[27] = contactInfo;
    $[28] = displayTermsAndConditions;
    $[29] = handleResend;
    $[30] = handleSubmit;
    $[31] = hint;
    $[32] = isReturningUser;
    $[33] = isVerifying;
    $[34] = resendMutation.isPending;
    $[35] = retryDisabledUntil;
    $[36] = verifyMutation.isPending;
    $[37] = verifyMutation.isSuccess;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  return t4;
}
function _temp10($_7) {
  return $_7.errorFallback.genericError.subtitle;
}
function _temp9($_6) {
  return $_6.identify.otp.errors.invalidCode;
}
function _temp8($_5) {
  return $_5.identify.otp.errors.codeExpired;
}
function _temp7($_4) {
  return $_4.errorFallback.genericError.title;
}
function _temp6($_8) {
  return $_8.identify.otp.legalFooter;
}
function _temp5($_1) {
  return $_1.errorFallback.genericError.subtitle;
}
function _temp4($_0) {
  return $_0.errorFallback.genericError.title;
}
function _temp3($_3) {
  return $_3.identify.otp.resendToast.description;
}
function _temp2($_2) {
  return $_2.identify.otp.resendToast.title;
}
function _temp($) {
  return $.identify.otp.challengeTakingLonger;
}